/**
 * Date and time helpers.
 *
 * Extracted from `formatTime12`, `translateDay`, `formatDateArabic`,
 * `convertDateToISO`, `convertDateToDisplay` and `getDaysRemaining`, which were
 * scattered across doctor_dashboard.js, app.js and patient_dashboard.js (several
 * of them defined more than once with drifting behaviour). These are the single
 * canonical implementations.
 *
 * Every function here is pure: no DOM, no globals, no clock reads except where a
 * `now` parameter is offered so tests can pin the clock.
 */

import { activeLang } from './locale';

export const ARABIC_DAYS = {
  sunday: 'الأحد',
  monday: 'الإثنين',
  tuesday: 'الثلاثاء',
  wednesday: 'الأربعاء',
  thursday: 'الخميس',
  friday: 'الجمعة',
  saturday: 'السبت',
};

/** Index order matches JavaScript's `Date.getDay()` (0 = Sunday). */
export const DAY_KEYS = [
  'sunday',
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
];

export const ARABIC_MONTHS = [
  'يناير',
  'فبراير',
  'مارس',
  'أبريل',
  'مايو',
  'يونيو',
  'يوليو',
  'أغسطس',
  'سبتمبر',
  'أكتوبر',
  'نوفمبر',
  'ديسمبر',
];

const pad2 = (n) => String(n).padStart(2, '0');

/**
 * Local-time `YYYY-MM-DD`. Never use `toISOString().slice(0, 10)` for this — it
 * shifts to UTC and silently moves the date for anyone east of Greenwich, which
 * includes every user of this system (Africa/Cairo).
 */
export function toDateKey(date = new Date()) {
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

/** Today's date key, in the browser's local timezone. */
export const todayKey = (now = new Date()) => toDateKey(now);

/** `YYYY-MM` for the month a date key belongs to. */
export function monthKey(dateKey) {
  return typeof dateKey === 'string' ? dateKey.slice(0, 7) : '';
}

/** Lowercase English weekday key for a `YYYY-MM-DD` string. */
export function dayKeyOf(dateKey) {
  if (!dateKey) return '';
  const d = new Date(`${dateKey}T00:00:00`);
  if (Number.isNaN(d.getTime())) return '';
  return DAY_KEYS[d.getDay()];
}

/** Arabic weekday name for a `YYYY-MM-DD` string. */
export function arabicDayOf(dateKey) {
  return ARABIC_DAYS[dayKeyOf(dateKey)] || '';
}

/** Translate an English day key ("monday") to Arabic. Unknown keys pass through. */
export function translateDay(dayKey) {
  if (!dayKey) return '';
  return ARABIC_DAYS[String(dayKey).toLowerCase()] || dayKey;
}

/**
 * "24:00" style -> "12:00 م" style, matching the legacy `formatTime12`.
 * Arabic markers: ص (AM) / م (PM).
 */
export function formatTime12(time24) {
  if (!time24) return '';
  const [hoursRaw, minutes = '00'] = String(time24).split(':');
  const parsed = parseInt(hoursRaw, 10);
  if (Number.isNaN(parsed)) return '';
  // The marker follows the reader's language (المرحلة 1.1); Arabic is as it always was.
  const marker = activeLang() === 'en' ? (parsed >= 12 ? 'PM' : 'AM') : parsed >= 12 ? 'م' : 'ص';
  const hour12 = parsed % 12 || 12;
  return `${hour12}:${minutes} ${marker}`;
}

/** "14:30" -> 870. Returns NaN for unparseable input. */
export function timeToMinutes(time24) {
  if (!time24) return Number.NaN;
  const [h, m] = String(time24).split(':').map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return Number.NaN;
  return h * 60 + m;
}

/** 870 -> "14:30". Wraps around a 24h day for values outside [0, 1440). */
export function minutesToTime(minutes) {
  const wrapped = ((Math.round(minutes) % 1440) + 1440) % 1440;
  return `${pad2(Math.floor(wrapped / 60))}:${pad2(wrapped % 60)}`;
}

/** "2026-03-15" -> "15 مارس 2026". */
export function formatDateArabic(dateKey) {
  if (!dateKey) return '';
  const d = new Date(`${dateKey}T00:00:00`);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getDate()} ${ARABIC_MONTHS[d.getMonth()]} ${d.getFullYear()}`;
}

/** "15/03/2026" -> "2026-03-15". Already-ISO input is returned unchanged. */
export function displayDateToIso(dateStr) {
  if (!dateStr) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return dateStr;
  const parts = String(dateStr).split('/');
  if (parts.length !== 3) return '';
  const [day, month, year] = parts;
  return `${year}-${pad2(Number(month))}-${pad2(Number(day))}`;
}

/** "2026-03-15" -> "15/03/2026". */
export function isoToDisplayDate(dateKey) {
  if (!dateKey || !/^\d{4}-\d{2}-\d{2}$/.test(dateKey)) return '';
  const [year, month, day] = dateKey.split('-');
  return `${day}/${month}/${year}`;
}

/**
 * Whole days from `now` until `target`, both floored to local midnight.
 * Negative when the target is in the past. Matches legacy `getDaysRemaining`.
 */
export function daysUntil(target, now = new Date()) {
  if (!target) return 0;
  const end = new Date(target instanceof Date ? target : `${target}T00:00:00`);
  if (Number.isNaN(end.getTime())) return 0;
  const start = new Date(now);
  start.setHours(0, 0, 0, 0);
  end.setHours(0, 0, 0, 0);
  return Math.ceil((end - start) / 86400000);
}

/** True when the date key is strictly before today. */
export const isPastDate = (dateKey, now = new Date()) => daysUntil(dateKey, now) < 0;

/** True when the date key is today. */
export const isToday = (dateKey, now = new Date()) => daysUntil(dateKey, now) === 0;

/** Add (or, for negative values, subtract) days from a date key. */
export function addDays(dateKey, delta) {
  const d = new Date(`${dateKey}T00:00:00`);
  if (Number.isNaN(d.getTime())) return '';
  d.setDate(d.getDate() + delta);
  return toDateKey(d);
}

/** Number of days in the month a `YYYY-MM` key refers to. */
export function daysInMonth(monthStr) {
  const [year, month] = String(monthStr).split('-').map(Number);
  if (!year || !month) return 0;
  return new Date(year, month, 0).getDate();
}

/** Every date key in a `YYYY-MM` month, in order. */
export function monthDateKeys(monthStr) {
  const total = daysInMonth(monthStr);
  return Array.from({ length: total }, (_, i) => `${monthStr}-${pad2(i + 1)}`);
}

/** Seconds elapsed since local midnight — the basis for every dose countdown. */
export function secondsSinceMidnight(now = new Date()) {
  return now.getHours() * 3600 + now.getMinutes() * 60 + now.getSeconds();
}

/** Seconds -> "HH:MM:SS", clamped at zero. */
export function formatCountdown(totalSeconds) {
  const s = Math.max(0, Math.floor(totalSeconds));
  return [Math.floor(s / 3600), Math.floor((s % 3600) / 60), s % 60].map(pad2).join(':');
}

/** Seconds-since-midnight -> "3:05 PM". */
export function formatSecondsAs12h(daySeconds) {
  const wrapped = ((Math.floor(daySeconds) % 86400) + 86400) % 86400;
  const hours = Math.floor(wrapped / 3600);
  const minutes = Math.floor((wrapped % 3600) / 60);
  const period = hours >= 12 ? 'PM' : 'AM';
  return `${hours % 12 || 12}:${pad2(minutes)} ${period}`;
}
